/**
 * Drag handler for Svelte 5 with enhanced performance
 * This file uses .svelte.ts extension to enable $effect usage
 */

interface DragHandlers {
  onDragStart?: (event: PointerEvent) => void;
  onDrag?: (event: PointerEvent) => void;
  onDragEnd?: (event: PointerEvent) => void;
  cursor?: string;
}

/**
 * Creates a high-performance drag handler with proper Svelte 5 reactivity
 * Uses pointer events for better touch support and requestAnimationFrame for smoothness
 * @param getElement - Function that returns the element (for reactive bindings)
 * @param handlers - Drag event handlers
 */
export function useDrag(
  getElement: () => HTMLElement | null | undefined,
  handlers: DragHandlers
) {
  let isDragging = false;
  let animationFrameId: number | null = null;
  let lastPointerEvent: PointerEvent | null = null;
  
  function handlePointerDown(event: PointerEvent) {
    const element = getElement();
    if (!element || !element.contains(event.target as Node)) return;
    
    // Capture pointer for this element
    element.setPointerCapture(event.pointerId);
    
    isDragging = true;
    handlers.onDragStart?.(event);
    
    if (handlers.cursor) {
      document.body.style.cursor = handlers.cursor;
    }
    
    // Disable text selection and transitions during drag
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    
    // Add pointer event listeners
    element.addEventListener('pointermove', handlePointerMove);
    element.addEventListener('pointerup', handlePointerUp);
    element.addEventListener('pointercancel', handlePointerUp);
  }
  
  function handlePointerMove(event: PointerEvent) {
    if (!isDragging) return;
    
    // Store the event for RAF callback
    lastPointerEvent = event;
    
    // Use requestAnimationFrame for smooth updates
    if (!animationFrameId) {
      animationFrameId = requestAnimationFrame(() => {
        if (lastPointerEvent && isDragging) {
          handlers.onDrag?.(lastPointerEvent);
        }
        animationFrameId = null;
      });
    }
  }
  
  function handlePointerUp(event: PointerEvent) {
    if (!isDragging) return;
    
    const element = getElement();
    if (element) {
      element.releasePointerCapture(event.pointerId);
      element.removeEventListener('pointermove', handlePointerMove);
      element.removeEventListener('pointerup', handlePointerUp);
      element.removeEventListener('pointercancel', handlePointerUp);
    }
    
    isDragging = false;
    handlers.onDragEnd?.(event);
    
    // Restore cursor and selection
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    
    // Cancel any pending animation frame
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    
    lastPointerEvent = null;
  }
  
  // Use $effect for proper reactivity
  $effect(() => {
    const element = getElement();
    if (!element) return;
    
    // Use pointer events for better performance and touch support
    element.addEventListener('pointerdown', handlePointerDown);
    
    // Add touch-action to prevent browser gestures
    element.style.touchAction = 'none';
    
    return () => {
      element.removeEventListener('pointerdown', handlePointerDown);
      element.removeEventListener('pointermove', handlePointerMove);
      element.removeEventListener('pointerup', handlePointerUp);
      element.removeEventListener('pointercancel', handlePointerUp);
      element.style.touchAction = '';
      
      // Clean up any pending animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      
      // Reset state
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      isDragging = false;
    };
  });
}